import {
  YESFI_CHECK_PATH_HIGHLIGHT,
  YESFI_CHECK_PATH_PRIMARY,
  YESFI_MARK_CENTER,
  YESFI_MARK_RADIUS,
  YESFI_RING_FAR_RADIUS,
  YESFI_RING_NEAR_RADIUS,
  YESFI_RING_STROKE_WIDTH,
} from '@/shared/ui/yesfiBrandMarkPaths'

/**
 * Color scheme for the YesFi brand mark — filled disc, two-tone check, and outer rings.
 * 关联文档文件：yesfiBrandMarkPaths.ts, YesFiWordmark.tsx
 */
export type YesfiBrandMarkColors = {
  circleFill: string
  checkPrimary: string
  checkHighlight: string
  ringColor: string
  ringOpacity: readonly [number, number]
}

/** Predefined brand mark color schemes matching sayfi-frontend. */
export const YESFI_BRAND_MARK_COLORS = {
  /** Dark backgrounds — standard brand blue */
  onBlue: {
    circleFill: '#0040FF',
    checkPrimary: '#FFFFFF',
    checkHighlight: '#4186FF',
    ringColor: '#0040FF',
    ringOpacity: [0.18, 0.36],
  },
  /** Light backgrounds — slightly deeper blue for contrast */
  onBlueLight: {
    circleFill: '#002AFF',
    checkPrimary: '#FFFFFF',
    checkHighlight: '#4186FF',
    ringColor: '#002AFF',
    ringOpacity: [0.2, 0.42],
  },
  /** White disc variant (for use on colored backgrounds) */
  onWhite: {
    circleFill: '#FFFFFF',
    checkPrimary: '#002AFF',
    checkHighlight: '#4186FF',
    ringColor: '#0040FF',
    ringOpacity: [0.18, 0.36],
  },
} as const satisfies Record<string, YesfiBrandMarkColors>

/**
 * Internal: renders the two-tone check mark with a clipPath to isolate it.
 * The check consists of a primary fill path and a highlight facet path.
 */
function YesfiBrandMarkCheck({ colors, clipId }: { colors: YesfiBrandMarkColors; clipId: string }) {
  return (
    <g clipPath={`url(#${clipId})`}>
      <path d={YESFI_CHECK_PATH_PRIMARY} fill={colors.checkPrimary} />
      <path d={YESFI_CHECK_PATH_HIGHLIGHT} fill={colors.checkHighlight} />
    </g>
  )
}

type GraphicProps = {
  colors?: YesfiBrandMarkColors
  clipId: string
}

/**
 * SVG `<g>` fragment — filled disc + two outer concentric rings + two-tone check mark.
 * Does not include `<svg>` wrapper; embed within a parent SVG that provides `<defs>`.
 */
export function YesfiBrandMarkGraphic({ colors = YESFI_BRAND_MARK_COLORS.onBlue, clipId }: GraphicProps) {
  const [ringFarOpacity, ringNearOpacity] = colors.ringOpacity
  const cx = YESFI_MARK_CENTER
  const cy = YESFI_MARK_CENTER

  return (
    <>
      <circle cx={cx} cy={cy} r={YESFI_MARK_RADIUS} fill={colors.circleFill} />
      <circle
        cx={cx}
        cy={cy}
        r={YESFI_RING_NEAR_RADIUS}
        fill="none"
        stroke={colors.ringColor}
        strokeWidth={YESFI_RING_STROKE_WIDTH}
        opacity={ringNearOpacity}
      />
      <circle
        cx={cx}
        cy={cy}
        r={YESFI_RING_FAR_RADIUS}
        fill="none"
        stroke={colors.ringColor}
        strokeWidth={YESFI_RING_STROKE_WIDTH}
        opacity={ringFarOpacity}
      />
      <YesfiBrandMarkCheck colors={colors} clipId={clipId} />
    </>
  )
}
